import React, { useState } from 'react';
import { User, Building2, Calendar, FileText, AlertCircle } from 'lucide-react';
import { Modal } from './Modal';
import { Button } from './Button';
import { AdminBookingStatus } from '../types';

interface BookingDetails {
  bookingId: number;
  providerId: number;
  userId: number;
  status: AdminBookingStatus;
  appointmentDate: string;
  serviceType?: string;
  userName?: string;
  providerName?: string;
  userEmail?: string;
  providerEmail?: string;
  providerTypeOfCare?: string[];
  providerLocation?: string | null;
  notes?: string;
  rejectionReason?: string;
  createdAt: string;
  updatedAt?: string;
  patientSnapshot?: {
    recipientName: string;
    recipientAge: number;
    recipientGender: string;
    mobilityStatus: string;
    medicalConditions: string[];
    equipmentDetails: string;
    address: string;
    city: string;
    emergencyContactName: string;
    emergencyContactPhone: string;
  };
  cancelReason?: string;
}

interface AdminBookingDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  booking: BookingDetails | null;
  onUpdateStatus: (bookingId: number, status: AdminBookingStatus) => void;
  onCancel?: (bookingId: number) => void;
  loading?: boolean;
}

export const AdminBookingDetailsModal: React.FC<AdminBookingDetailsModalProps> = ({
  isOpen,
  onClose,
  booking,
  onUpdateStatus,
  loading = false,
}) => {
  const [showStatusConfirm, setShowStatusConfirm] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<AdminBookingStatus | null>(null);

  if (!booking) return null;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleStatusChange = (newStatus: AdminBookingStatus) => {
    setSelectedStatus(newStatus);
    setShowStatusConfirm(true);
  };

  const confirmStatusChange = () => {
    if (selectedStatus) {
      onUpdateStatus(booking.bookingId, selectedStatus);
      setShowStatusConfirm(false);
      setSelectedStatus(null);
    }
  };

  const availableStatuses: AdminBookingStatus[] = ['pending', 'confirmed', 'completed', 'cancelled'];
  const canUpdateStatus = booking.status != null && booking.status !== 'cancelled' && booking.status !== 'completed';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Booking #${booking.bookingId}`}>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
              booking.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
              booking.status === 'confirmed' ? 'bg-blue-100 text-blue-800' :
              booking.status === 'completed' ? 'bg-green-100 text-green-800' :
              'bg-red-100 text-red-800'
            }`}>
              {booking.status ? booking.status.charAt(0).toUpperCase() + booking.status.slice(1) : 'Unknown'}
            </span>
          </div>
          <div className="text-sm text-gray-500">
            Created: {formatDateTime(booking.createdAt)}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center mb-3">
              <User className="h-5 w-5 text-gray-400 mr-2" />
              <h3 className="font-semibold text-gray-900">User Information</h3>
            </div>
            <div className="space-y-2">
              <p className="text-sm">
                <span className="font-medium text-gray-700">Name:</span>{' '}
                <span className="text-gray-900">{booking.userName || 'Unknown'}</span>
              </p>
              {booking.userEmail && (
                <p className="text-sm">
                  <span className="font-medium text-gray-700">Email:</span>{' '}
                  <span className="text-gray-900">{booking.userEmail}</span>
                </p>
              )}
              <p className="text-sm">
                <span className="font-medium text-gray-700">User ID:</span>{' '}
                <span className="text-gray-900">{booking.userId}</span>
              </p>
            </div>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center mb-3">
              <Building2 className="h-5 w-5 text-gray-400 mr-2" />
              <h3 className="font-semibold text-gray-900">Provider Information</h3>
            </div>
            <div className="space-y-2">
              <p className="text-sm">
                <span className="font-medium text-gray-700">Name:</span>{' '}
                <span className="text-gray-900">{booking.providerName || 'Unknown'}</span>
              </p>
              {booking.providerEmail && (
                <p className="text-sm">
                  <span className="font-medium text-gray-700">Email:</span>{' '}
                  <span className="text-gray-900">{booking.providerEmail}</span>
                </p>
              )}
              {booking.providerLocation && (
                <p className="text-sm">
                  <span className="font-medium text-gray-700">Location:</span>{' '}
                  <span className="text-gray-900">{booking.providerLocation}</span>
                </p>
              )}
              {booking.providerTypeOfCare && booking.providerTypeOfCare.length > 0 && (
                <div className="text-sm">
                  <span className="font-medium text-gray-700">Care Types:</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {booking.providerTypeOfCare.map((type, idx) => (
                      <span key={idx} className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full text-xs">
                        {type}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              <p className="text-sm">
                <span className="font-medium text-gray-700">Provider ID:</span>{' '}
                <span className="text-gray-900">{booking.providerId}</span>
              </p>
            </div>
          </div>
        </div>

        <div className="bg-blue-50 p-4 rounded-lg">
          <div className="flex items-center mb-3">
            <Calendar className="h-5 w-5 text-blue-600 mr-2" />
            <h3 className="font-semibold text-gray-900">Booking Details</h3>
          </div>
          <div className="space-y-2">
            <p className="text-sm">
              <span className="font-medium text-gray-700">Appointment Date:</span>{' '}
              <span className="text-gray-900">{formatDate(booking.appointmentDate)}</span>
            </p>
            {booking.serviceType && (
              <p className="text-sm">
                <span className="font-medium text-gray-700">Service Type:</span>{' '}
                <span className="text-gray-900">{booking.serviceType}</span>
              </p>
            )}
          </div>
        </div>

        {booking.notes && (
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center mb-3">
              <FileText className="h-5 w-5 text-gray-400 mr-2" />
              <h3 className="font-semibold text-gray-900">Notes</h3>
            </div>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{booking.notes}</p>
          </div>
        )}

        {booking.patientSnapshot && (
          <div className="bg-green-50 p-4 rounded-lg">
            <h3 className="font-semibold text-gray-900 mb-3">Care Recipient Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <p className="text-sm">
                  <span className="font-medium text-gray-700">Name:</span>{' '}
                  <span className="text-gray-900">{booking.patientSnapshot.recipientName}</span>
                </p>
                <p className="text-sm">
                  <span className="font-medium text-gray-700">Age:</span>{' '}
                  <span className="text-gray-900">{booking.patientSnapshot.recipientAge}</span>
                </p>
                <p className="text-sm">
                  <span className="font-medium text-gray-700">Gender:</span>{' '}
                  <span className="text-gray-900">{booking.patientSnapshot.recipientGender}</span>
                </p>
                <p className="text-sm">
                  <span className="font-medium text-gray-700">Mobility:</span>{' '}
                  <span className="text-gray-900">{booking.patientSnapshot.mobilityStatus}</span>
                </p>
              </div>
              <div className="space-y-2">
                <p className="text-sm">
                  <span className="font-medium text-gray-700">Location:</span>{' '}
                  <span className="text-gray-900">
                    {booking.patientSnapshot.address}, {booking.patientSnapshot.city}
                  </span>
                </p>
                <p className="text-sm">
                  <span className="font-medium text-gray-700">Emergency Contact:</span>{' '}
                  <span className="text-gray-900">{booking.patientSnapshot.emergencyContactName}</span>
                </p>
                <p className="text-sm">
                  <span className="font-medium text-gray-700">Contact Phone:</span>{' '}
                  <span className="text-gray-900">{booking.patientSnapshot.emergencyContactPhone}</span>
                </p>
              </div>
              {booking.patientSnapshot.medicalConditions.length > 0 && (
                <div className="col-span-2">
                  <p className="text-sm font-medium text-gray-700 mb-1">Medical Conditions:</p>
                  <div className="flex flex-wrap gap-2">
                    {booking.patientSnapshot.medicalConditions.map((condition, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-white rounded-full text-xs text-gray-700"
                      >
                        {condition}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {booking.patientSnapshot.equipmentDetails && (
                <div className="col-span-2">
                  <p className="text-sm">
                    <span className="font-medium text-gray-700">Equipment:</span>{' '}
                    <span className="text-gray-900">{booking.patientSnapshot.equipmentDetails}</span>
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {booking.rejectionReason && (
          <div className="bg-orange-50 p-4 rounded-lg">
            <div className="flex items-center mb-2">
              <AlertCircle className="h-5 w-5 text-orange-600 mr-2" />
              <h3 className="font-semibold text-orange-900">Rejection Reason</h3>
            </div>
            <p className="text-sm text-orange-700">{booking.rejectionReason}</p>
          </div>
        )}

        {booking.cancelReason && (
          <div className="bg-red-50 p-4 rounded-lg">
            <div className="flex items-center mb-2">
              <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
              <h3 className="font-semibold text-red-900">Cancellation Reason</h3>
            </div>
            <p className="text-sm text-red-700">{booking.cancelReason}</p>
          </div>
        )}

        {canUpdateStatus && (
          <div className="border-t pt-6">
            <h3 className="font-semibold text-gray-900 mb-4">Update Booking Status</h3>
            <div className="flex flex-wrap gap-3">
              {availableStatuses
                .filter((status) => status !== booking.status)
                .map((status) => (
                  <Button
                    key={status}
                    onClick={() => handleStatusChange(status)}
                    variant={status === 'cancelled' ? 'danger' : 'primary'}
                    disabled={loading}
                  >
                    {status === 'cancelled' ? 'Cancel Booking' : `Mark as ${status.charAt(0).toUpperCase() + status.slice(1)}`}
                  </Button>
                ))}
            </div>
          </div>
        )}

        {showStatusConfirm && selectedStatus && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center mb-2">
              <AlertCircle className="h-5 w-5 text-yellow-600 mr-2" />
              <h4 className="font-semibold text-yellow-900">Confirm Status Change</h4>
            </div>
            <p className="text-sm text-yellow-800 mb-4">
              Are you sure you want to change the booking status to{' '}
              <strong>{selectedStatus}</strong>?
            </p>
            <div className="flex gap-3">
              <Button onClick={confirmStatusChange} disabled={loading}>
                Confirm
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setShowStatusConfirm(false);
                  setSelectedStatus(null);
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3 border-t pt-6">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
};
